import {
	columnVisibilityFeature,
	createColumnHelper,
	metaHelper,
	rowSortingFeature,
	tableFeatures,
} from '@tanstack/react-table';
import { Link as LinkIcon, RotateCcw, ZoomIn } from 'lucide-react';
import type React from 'react';

import type { DamageType } from '@/common/damage';
import { DAMAGE_CLASSES } from '@/common/options';
import type { DeletedBy, WithDeletedBy } from '@/common/soft-delete';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import type { DataTableColumnMeta } from '@/components/data-table/data-table-features';
import type { SortFieldMap } from '@/components/data-table/sort-param';
import { DeletedOnIcon } from '@/components/soft-delete/DeletedInfo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { BulkSelection } from '@/hooks/use-bulk-selection';
import { t } from '@/i18n';
import { cn } from '@/lib/utils';

export type DamageListItem = {
	id: string;
	title: string;
	damageClass?: string;
	hasPolygons: boolean;
	materialDetail?: DamageType['materialDetail'];
	statusDetail?: DamageType['statusDetail'];
	deletedAt?: string | null;
	deletedBy?: DeletedBy;
};

export interface DamageTableMeta {
	handleHighlightDamage: (damageId: string, hasPolygons: boolean, event: React.MouseEvent) => void;
	handleCopyDamageLink: (damageId: string, event: React.MouseEvent) => void;
	/** Bulk-delete selection; the `select` column is hidden for users who can't delete. */
	selection: BulkSelection;
	/** Deleted-items view (GH #331): the actions column offers Restore instead of map and link */
	handleRestore?: (damage: DamageListItem) => void;
}

// Sorting is server-side (`manualSorting`), so no sorted row model or sort fns are registered.
export const damageTableFeatures = tableFeatures({
	columnVisibilityFeature,
	rowSortingFeature,
	tableMeta: metaHelper<DamageTableMeta>(),
	columnMeta: metaHelper<DataTableColumnMeta>(),
});

export function toDamageListItem(damage: WithDeletedBy<DamageType>): DamageListItem {
	return {
		id: damage.id ?? '',
		title: damage.title ?? '',
		damageClass: damage.class,
		hasPolygons: !!damage.hasPolygons,
		materialDetail: damage.materialDetail,
		statusDetail: damage.statusDetail,
		deletedAt: damage.deletedAt,
		deletedBy: damage.deletedBy,
	};
}

// Column id → backend `sort` field. Must match DAMAGE_SORT_FIELDS in the backend DTO.
export const DAMAGE_SORT_FIELDS: SortFieldMap = {
	title: 'title',
	material: 'materialDetail.category',
	status: 'statusDetail.label',
};

/** Matches on the leading class number ("2 - langfristige Gefährdung" → "2"). */
function getDamageClassColor(damageClass?: string) {
	const classNumber = damageClass?.trim().charAt(0);
	return DAMAGE_CLASSES.find((c) => c.value.charAt(0) === classNumber)?.color ?? 'bg-muted-foreground/50';
}

const columnHelper = createColumnHelper<typeof damageTableFeatures, DamageListItem>();

export const damageColumns = columnHelper.columns([
	columnHelper.display({
		id: 'select',
		header: ({ table }) => {
			const selection = table.options.meta?.selection;
			if (!selection) return null;
			return (
				<Checkbox
					checked={selection.allSelected ? true : selection.someSelected ? 'indeterminate' : false}
					onCheckedChange={selection.toggleAll}
					aria-label={t('damages.list.selectAll')}
				/>
			);
		},
		cell: ({ row, table }) => {
			const selection = table.options.meta?.selection;
			if (!selection) return null;
			return (
				<Checkbox
					checked={selection.isSelected(row.original.id)}
					onCheckedChange={() => selection.toggle(row.original.id)}
					aria-label={t('damages.list.selectRow', {
						title: row.original.title || t('common.entities.damage', { count: 1 }),
					})}
				/>
			);
		},
		meta: { stopRowClick: true, className: 'py-3', headerClassName: 'w-10' },
	}),
	columnHelper.accessor('title', {
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('damages.list.columnDamage')} />,
		cell: ({ row }) => {
			const damage = row.original;
			return (
				<div className="min-w-0">
					<div className="flex items-center gap-2 min-w-0">
						<div className={cn('w-3 h-3 rounded-full shrink-0', getDamageClassColor(damage.damageClass))} />
						<span className="text-foreground font-medium truncate" title={damage.title}>
							{damage.title}
						</span>
						<DeletedOnIcon deletedAt={damage.deletedAt} deletedBy={damage.deletedBy} />
					</div>
				</div>
			);
		},
		meta: { className: 'py-3 max-w-0', headerClassName: 'w-1/4' },
	}),
	columnHelper.accessor((row) => row.materialDetail?.category ?? t('common.states.unknown'), {
		id: 'material',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('damages.list.columnMaterial')} />,
		meta: { className: 'text-muted-foreground py-3 max-w-0 truncate', headerClassName: 'w-1/4' },
	}),
	columnHelper.accessor((row) => row.statusDetail?.label ?? t('common.states.unknown'), {
		id: 'status',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.status')} />,
		cell: ({ getValue }) => (
			<Badge variant="outline" className="bg-background font-normal max-w-full flex">
				<span className="truncate" title={getValue()}>
					{getValue()}
				</span>
			</Badge>
		),
		meta: { className: 'py-3 max-w-0', headerClassName: 'w-1/4' },
	}),
	columnHelper.display({
		id: 'actions',
		cell: ({ row, table }) => {
			const damage = row.original;
			const tableMeta = table.options.meta;
			if (tableMeta?.handleRestore) {
				return (
					<div className="flex justify-end">
						<Button variant="outline" size="sm" onClick={() => tableMeta.handleRestore?.(damage)}>
							<RotateCcw />
							{t('common.actions.restore')}
						</Button>
					</div>
				);
			}
			return (
				<div className="flex items-center gap-1 justify-end">
					<Button
						variant="outline"
						size="icon"
						className={cn(
							'size-7 rounded transition-colors',
							damage.hasPolygons ? 'cursor-pointer' : 'cursor-not-allowed opacity-30 bg-muted/50',
						)}
						onClick={(e) => tableMeta?.handleHighlightDamage(damage.id, damage.hasPolygons, e)}
						title={damage.hasPolygons ? t('damages.list.viewOnMap') : t('damages.list.noMapData')}
					>
						<ZoomIn className="size-3.5 text-muted-foreground" />
					</Button>
					<Button
						variant="outline"
						size="icon"
						onClick={(e) => tableMeta?.handleCopyDamageLink(damage.id, e)}
						className="size-7 rounded transition-colors"
						title={t('damages.detail.copyLink')}
					>
						<LinkIcon className="size-3.5 text-muted-foreground" />
					</Button>
				</div>
			);
		},
		meta: { className: 'py-3', headerClassName: 'w-1/4', stopRowClick: true },
	}),
]);
